<template>
  <div>
    <template>
      <div class="modal is-active"
        role="dialog"
        aria-label="Remove User"
        aria-hidden="false"
        ref="modal"
        v-if="showModal"
      >
        <div class="modal-background"></div>
        <div class="modal-card">
          <header class="modal-card-head">
            <p class="modal-card-title">Remove User</p>
            <button
              class="icon is-right"
              @click.prevent="toggleModal"
              ref="closeIcon"
            >
              <font-awesome-icon :icon="['fal', 'times']" title="Close" />
            </button>
          </header>
          <section class="modal-card-body">
            <div class="modal-body">
              You cannot recover deleted users.
             <br>Are you sure you want to delete this user?
            </div>
            <div class="update-box">
              <button
                class="button button-link"
                @click.prevent="toggleModal"
              >
                Cancel
              </button>
              <button
                class="button is-primary"
                @click="deletePendingUser()">
                  Remove User
              </button>
            </div>
          </section>
        </div>
      </div>
    </template>

    <div class="document">
      <div class="header">
        <EsHeader>
          <template #header>
            <div class="header-title">User Manager</div>
          </template>
          <template #actions>
            <ul>
              <li>
                <div class="list-content">
                  <div class="list-header">
                    <div class="list-title is-header">
                      <span class="is-primary">
                        {{ totalProfiles }}
                      </span>
                    </div>
                    <div class="list-subtitle">Users</div>
                  </div>
                </div>
              </li>
              <li v-if="hasModule('SYS:SEC:USRMGR:ADD')" class="p-narrow">
                <div class="list-content">
                  <button class="button is-primary"
                    @click="$router.push(`/${activeModule.name}/addOption`)"
                    ref="linkAddRecords">
                    Add Records
                  </button>
                </div>
              </li>
            </ul>
          </template>
        </EsHeader>
      </div>
      <es-filter :defaultExpanded="filterExpanded">
        <template #filter-options>
          <div class="columns is-3-desktop multi-dropdown-columns">
            <div class="column is-3">
              <div class="field">
                <label class="label has-text-weight-bold"
                  for="filterUserEmailLn"
                >
                  Email or Last Name:
                </label>
                <div class="control">
                  <input id="filterUserEmailLn" class="input" type="text" v-model="searchName">
                </div>
              </div>
            </div>

            <div class="column is-3">
              <es-select
                label="Role:"
                v-bind:multi="true"
                api="roles"
                v-on:update:values="roles = $event"
                ref="selRoles"
                valueTpl="${name}"
                :preSelected="roleCodes"
                keyValue="id"
                v-bind:clearOut="true"
              />
            </div>

            <div class="column is-3">
              <es-select
                label="Account Status:"
                v-bind:multi="true"
                valueTpl="${status}"
                :setValues="accountStatuses"
                :preSelected="statusInput"
                keyValue="id"
                v-on:update:values="statusInput = $event"
                ref="selStatuses"
                v-bind:clearOut="true"
              />
            </div>

            <div class="column is-3">
              <div class="field">
                <label class="label has-text-weight-bold">Log In Status: </label>
                <div class="control">
                  <input id="filterLogIn" type="checkbox" v-model="active">
                  <label style="margin-left: 1rem" for="filterLogIn">Currently Logged In</label>
                </div>
              </div>
            </div>
          </div>

          <div class="columns is-3-desktop multi-dropdown-columns">
            <div class="column is-3">
              <es-select
                :label="`District:`"
                v-bind:multi="true"
                v-bind:search="true"
                api="districts"
                v-on:update:values="districts = $event"
                ref="selDistricts"
                :valueTplFn="(result) => `${result.location.name} (${result.code})`"
                :preSelected="districtCodes"
                searchMinLength="2"
                keyValue="code"
              />
            </div>

            <div class="column is-3">
              <es-select
                :label="`School:`"
                v-bind:multi="true"
                v-bind:search="true"
                api="schools"
                :apiQueryParams="getDistrictParams"
                v-on:update:values="schools = $event"
                ref="selSchools"
                :valueTplFn="(result) => `${result.location.name} (${result.code})`"
                :subValueTplFn="(result) =>
                  `${result.districtLocation.name} (<i>${result.districtCode }</i>)`"
                :preSelected="schoolCodesPromises"
                searchMinLength="2"
                :key-value="['districtCode', 'code']"
              />
            </div>

            <div class="column is-3">
              <es-select
                label="Location Group:"
                v-bind:multi="true"
                v-bind:search="true"
                api="location-groups"
                :apiQueryParams="apiQueryParams"
                v-on:update:values="groups = $event"
                ref="selGroups"
                valueTpl="${label}"
                :preSelected="groupCodes"
                searchMinLength="3"
                keyValue="id"
              />
            </div>

            <div class="column" style="margin-top:15px">
              <div class="field is-grouped flex-justify--end">
                <div class="control is-flex flex-align--center">
                  <a tabindex="0" role="button"
                    @click.prevent="resetFilter()"
                  >
                    Reset
                  </a>
                </div>
                <div class="control">
                  <button
                    class="button is-primary"
                    @click="filterData()">
                    Filter Data
                  </button>
                </div>
              </div>
            </div>
          </div>
        </template>
      </es-filter>
      <div class="content">
        <div class="table-wrapper">
          <table
            class="table is-fullwidth"
            :class="{ 'is-hoverable' : profiles.length }">
            <es-sorter
              :apiSorter="apiSorter"
              :columnHeaders="columnHeaders"
              @updateSorter="updateSorter"/>
            <tbody v-if="loading">
              <tr>
                <td colspan="7" class="has-text-centered">
                  <es-loader style="margin: 6.5rem"/>
                </td>
              </tr>
            </tbody>
            <tbody v-if="!loading && loadingError ">
              <tr>
                <td colspan="7" class="has-text-centered">
                  <div class="no-result-container">
                    <div class="icon">
                      <font-awesome-icon :icon="['fal', 'exclamation-triangle']" size="4x"/>
                    </div>
                    <p></p>
                    <h4>There was an issue. Please try again or contact your administrator.</h4>
                  </div>
                </td>
              </tr>
            </tbody>
            <tbody v-if="!loading && !loadingError && !profiles.length">
              <tr>
                <td colspan="7" class="has-text-centered">
                  <div class="no-result-container">
                    <div class="icon">
                      <font-awesome-icon :icon="['fal', 'exclamation-triangle']" size="4x"/>
                    </div>
                    <p></p>
                    <h4>No results were found.</h4>
                  </div>
                </td>
              </tr>
            </tbody>
            <tbody v-if="!loading && !loadingError && profiles.length">
              <tr v-for="profile in this.profiles"
                :class="{'active': profile.isNewList}" v-bind:key="profile.id">
                <td>
                  <router-link
                    class="is-primary"
                    :to="{ path: `user/${profile.id}` }"
                    rel="tooltip" title="Edit User Profile"
                  >
                    {{ profile.email }}
                  </router-link>
                </td>
                <td class="max-width-20" style="width: auto">{{ profile.firstName }}</td>
                <td class="max-width-20" style="width: auto">{{ profile.lastName }}</td>
                <td class="is-capitalized">{{ profile.status.toLowerCase() }}</td>
                <td>{{ profile.lastAccessedDate | formatDate }}</td>
                <td>
                  <span class="tags" >
                    <strong
                      class="tag"
                      v-for="role in profile.locationPermissions"
                      v-bind:key="role.id"
                    >
                      {{ (role.role.name) }}
                    </strong>
                  </span>
                </td>
                <td class="table-action" style="text-align: end">
                  <div
                    class="icon center-icon mr-2"
                    v-if="profile.status === 'ACTIVE'
                      && hasModule('SYS:SEC:USRMGR:RESETPWD')"
                  >
                    <a tabindex="0" role="button"
                      @click.prevent='resetPassword(`${profile.id}`)'
                      @keypress.enter.prevent='resetPassword(`${profile.id}`)'>
                      <font-awesome-icon
                        :icon="['far', 'key']"
                        class="fa-lg"
                        title="Reset Password for the User"
                      />
                    </a>
                  </div>
                  <div
                    class="icon center-icon mr-2"
                    v-if="profile.status === 'PENDING'
                      && hasModule('SYS:SEC:USRMGR:EDIT')"
                  >
                    <a tabindex="0" role="button"
                      @click.prevent='toggleModal(`${profile.id}`)'
                      @keypress.enter.prevent='toggleModal(`${profile.id}`)'
                      :ref="`linkDelete${profile.id}`">
                      <font-awesome-icon
                        :icon="['far', 'trash-alt']"
                        style="font-size: 1.2rem;"
                        class="fa-lg"
                        title="Delete Pending User"
                      />
                    </a>
                  </div>
                  <div
                    class="icon center-icon mr-2"
                    v-if="profile.status === 'PENDING'
                      && hasModule('SYS:SEC:USRMGR:ADD')"
                  >
                    <a tabindex="0" role="button"
                      @click.prevent='resendSignupEmail(`${profile.id}`)'
                      @keypress.enter.prevent='resendSignupEmail(`${profile.id}`)'>
                      <font-awesome-icon
                        :icon="['far', 'paper-plane']"
                        style="font-size: 1.1rem;"
                        class="fa-lg"
                        title="Resend Sign-up Email to the User"
                      />
                    </a>
                  </div>
                  <div class="icon center-icon mr-2" v-if="hasModule('SYS:SEC:USRMGR:VIEW')">
                    <router-link
                      :to="{ path: `user/${profile.id}` }"
                    >
                      <font-awesome-icon
                        :icon="['far', 'angle-double-right']"
                        style="font-size: 1.8rem;"
                        class="fa-2x"
                        title="View Details"
                      />
                    </router-link>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="pager-container" v-if="!loading && !loadingError && profiles.length">
          <es-pager :totalCount="totalProfiles" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faBan,
  faCheck,
  faCheckCircle,
  faExclamationTriangle,
  faEllipsisH,
  faPauseCircle,
  faSearch,
  faSpinner,
} from '@fortawesome/pro-light-svg-icons';
import {
  faTrashAlt,
  faPaperPlane,
  faAngleDoubleRight,
  faKey,
} from '@fortawesome/pro-regular-svg-icons';
import axios from 'axios';
import qs from 'qs';
import moment from 'moment';
import EsHeader from '@/components/EsHeader.vue';
import EsSorter from '@/components/EsSorter.vue';
import EsPager from '@/components/EsPager.vue';
import EsFilter from '@/components/EsFilter.vue';
import EsSelect from '@/components/EsSelect.vue';
import EsLoader from '@/components/EsLoader.vue';
import FieldLabels from '@/mixins/FieldLabels';
import NotificationManager from '@/mixins/NotificationManager';
import PermissionManager from '@/mixins/PermissionManager';
import AccessibilityUtils from '@/mixins/AccessibilityUtils';

library.add(
  faBan,
  faCheck,
  faCheckCircle,
  faExclamationTriangle,
  faEllipsisH,
  faPaperPlane,
  faKey,
  faPauseCircle,
  faSearch,
  faSpinner,
  faTrashAlt,
  faAngleDoubleRight,
);

export default {
  mixins: [
    FieldLabels,
    NotificationManager,
    PermissionManager,
    AccessibilityUtils,
  ],
  components: {
    EsHeader,
    EsSorter,
    EsSelect,
    EsPager,
    EsFilter,
    EsLoader,
  },
  props: {
    activeRoles: {
      type: Array,
      required: true,
    },
    page: {
      type: Number,
    },
    search: {
      type: String,
    },
    isActive: {
      type: Boolean,
      // default: true,
    },
    roleCodes: {
      type: Array,
    },
    districtCodes: {
      type: Array,
    },
    schoolCodes: {
      type: Array,
    },
    groupCodes: {
      type: Array,
    },
    statuses: {
      type: Array,
    },
    sortField: {
      type: String,
    },
    sortOrder: {
      type: String,
    },
  },
  data() {
    const ACCOUNT_STATUSES = [
      { status: 'Active', id: 'A' },
      { status: 'Inactive', id: 'I' },
      { status: 'Pending', id: 'P' },
    ];
    return {
      modalUserID: '',
      searchName: this.search,
      active: this.isActive,
      districts: this.districtCodes.map(d => ({ code: d })),
      schools: this.schoolCodes.map(code => ({ districtCode: code.split(/[:]+/)[1], code: code.split(/[:]+/)[0] })),
      groups: this.groupCodes.map(g => ({ id: g })),
      roles: this.roleCodes.map(r => ({ id: r })),
      statusInput: ACCOUNT_STATUSES.filter(s => this.statuses.find(status => status === s.id)),
      filterExpanded: false,

      accountStatuses: ACCOUNT_STATUSES,
      apiQueryParams: { active: 'true' },

      loading: true,
      loadingError: null,
      status: 'all',
      userId: '',
      showModal: false,
      profiles: [],
      totalProfiles: 0,

      apiSorter: {},
      columnHeaders: [
        {
          header: 'Email',
          title: 'Email',
          field: 'email',
          order: null,
          sortable: true,
          style: {
            width: '20%',
          },
        },
        {
          header: 'First Name',
          title: 'First Name',
          field: 'firstName',
          order: null,
          sortable: true,
          style: {
            width: '15%',
          },
        },
        {
          header: 'Last Name',
          title: 'Last Name',
          field: 'lastName',
          order: null,
          sortable: true,
          style: {
            width: '15%',
          },
        },
        {
          header: 'Status',
          title: 'Status',
          field: 'status',
          order: null,
          sortable: true,
          style: {
            width: '10%',
          },
        },
        {
          header: 'Last Login ',
          title: 'Last Login ',
          field: 'lastAccessedDate',
          order: null,
          sortable: true,
          style: {
            idth: '10%',
          },
        },
        {
          header: 'Role ',
          title: 'Role ',
          field: null,
          style: {
            width: '20%',
          },
        },
        {
          header: 'Actions ',
          title: 'Actions ',
          field: null,
          style: {
            width: '10%',
          },
        },
      ],
    };
  },
  watch: {
    $route: 'onSubmit',
  },
  created() {
    this.onLoad();
    if (Object.keys(this.searchParams).length) {
      this.filterExpanded = true;
    }
  },
  asyncComputed: {
    async schoolCodesPromises() {
      const promises = [];
      this.schoolCodes.forEach(code => promises.push(axios.get(`districts/${code.split(/[:]+/)[1]}/schools/${code.split(/[:]+/)[0]}`)));
      const responses = await Promise.all(promises);
      return responses.map(r => r.data);
    },
  },
  computed: {
    ...mapGetters({
      activeModule: 'appModules/getActiveModule',
    }),
    getSorter() {
      return this.columnHeaders.find(header => header.order);
    },
    getDistrictParams() {
      const params = {};
      params.districtCode = this.districts.map(d => d.code);

      return params;
    },
    hasInputParams() {
      return this.searchName || this.active || this.districts.length || this.statusInput.length
        || this.schools.length || this.page || this.groups.length || this.roles.length;
    },
    hasUrlParams() {
      return this.page || this.search || this.isActive || this.roleCodes || this.districtCodes
        || this.schoolCodes || this.groupCodes || this.statuses || this.sortField || this.sortOrder;
    },
    searchParams() {
      const params = {};

      if (this.searchName) {
        params.search = this.searchName;
      }

      if (this.active) {
        params.isActive = this.active;
      }

      if (this.districts.length) {
        params.districtCode = this.districts.map(d => d.code);
      }

      if (this.schools.length) {
        params.schoolCode = this.schools.map(s => `${s.code}:${s.districtCode}`);
      }

      if (this.statusInput.length) {
        params.status = this.statusInput.map(s => s.id);
      }

      if (this.groups.length) {
        params.group = this.groups.map(g => g.id);
      }

      if (this.roles.length) {
        params.role = this.roles.map(r => r.id);
      }

      if (this.getSorter) {
        params.sortField = this.getSorter.field;
        params.sortOrder = this.getSorter.order;
      }

      return params;
    },
    urlParams() {
      const params = {};

      params.pageSize = 25;
      params.page = this.page ? this.page : undefined;
      params.search = this.search ? this.search : undefined;
      params.isActive = this.isActive ? this.isActive : undefined;
      params.districtCode = this.districtCodes.length ? this.districtCodes : undefined;
      params.schoolCode = this.schoolCodes.length ? this.getSchoolCodes() : undefined;
      params.status = this.statuses.length ? this.statuses : undefined;
      params.group = this.groupCodes.length ? this.groupCodes : undefined;
      params.role = this.roleCodes.length ? this.roleCodes : undefined;

      params.sort = JSON.stringify([{
        field: this.sortField,
        order: this.sortOrder,
      }]);

      return params;
    },
  },
  methods: {
    ...mapActions([
      'getUserProfiles',
      'forceResetPassword',
      'resendSignupEmailForUser',
      'getRoles',
    ]),
    async onLoad() {
      try {
        this.loading = true;
        this.loadingError = false;

        const response = await this.executeSearch();
        this.apiSorter = response.data.sorters[0];
        this.profiles = response.data.data;
        this.totalProfiles = response.data.totalCount;
      } catch (e) {
        this.loadingError = true;
      } finally {
        this.loading = false;
      }
    },
    filterData() {
      if (this.hasInputParams) {
        this.doSearch();
      } else if (!this.hasInputParams && this.hasUrlParams) {
        this.resetFilter();
      }
    },
    doSearch() {
      this.$router.replace({ query: this.searchParams });
    },
    async getSchools(schools) {
      const splitSchoolCodes = [];
      const promises = [];
      for (let i = 0; i < schools.length; i += 1) {
        splitSchoolCodes.push(schools[i].split(/[:]+/));
        promises.push(
          axios.get(`districts/${splitSchoolCodes[i][1]}/schools/${splitSchoolCodes[i][0]}`).then((response) => {
            this.schools.push(response.data);
          }),
        );
      }
      Promise.all(promises);
    },
    // calls when url is updated
    executeSearch() {
      return axios.get('users',
        {
          params: this.urlParams,
          paramsSerializer(params) {
            return qs.stringify(params, { arrayFormat: 'repeat' });
          },
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache',
            'Expires': '0',
          },
        });
    },
    getSchoolCodes() {
      return this.schoolCodes.map(code => code.split(/[:]+/)[0]);
    },
    reLoad() {
      this.onSubmit();
      if (this.$router.currentRoute.params.message) {
        this.showSuccess(this.$router.currentRoute.params.message);
      }
    },
    onSubmit() {
      return this.hasModule('SYS:SEC:USRMGR:VIEW') ? this.onLoad() : false;
    },
    resetFilter() {
      if (!this.hasInputParams && this.hasUrlParams) {
        this.$router.replace({ query: {} });
      }
      if (Object.keys(this.searchParams).length) {
        this.$router.replace({ query: {} });
      }

      this.$refs.selDistricts.clear();
      this.$refs.selGroups.clear();
      this.$refs.selRoles.clear();
      this.$refs.selSchools.clear();
      this.$refs.selStatuses.clear();

      this.status = '';
      this.active = false;
      this.searchName = '';

      // this.profiles = [];
    },
    async resetPassword(id) {
      try {
        await this.forceResetPassword(id);
        this.showSuccess('Reset Password is done.');
      } catch (error) {
        this.showError(error.response.data.message);
      }
    },
    async resendSignupEmail(id) {
      try {
        await this.resendSignupEmailForUser(id);
        this.showSuccess('Account Sign-up email has been sent to the user.');
      } catch (error) {
        this.showError(error.response.data.message);
      }
    },
    async deletePendingUser() {
      try {
        await axios.delete(`/users/${this.userId}`);
        this.showSuccess('The user profile has been deleted successfully.');
      } catch (error) {
        this.showError(error.response.data.message);
      }
      this.toggleModal();
      this.reLoad();
    },
    updateSorter(header) {
      this.$router.replace({ query: this.searchParams });
    },
    updateStatusForProfile(id, newStatus) {
      const i = parseInt(id, 10);
      const index = this.profiles.findIndex(p => p.id === i);
      if (index > -1) {
        this.profiles[index].status = newStatus;
      }
    },
    showError(error) {
      this.showNotification('danger', Array.isArray(error) ? error : [error]);
    },
    showSuccess(message) {
      this.showNotification('success', Array.isArray(message) ? message : [message]);
    },
    toggleModal(id) {
      this.userId = id;
      this.showModal = !this.showModal;
      this.$nextTick(() => {
        if (this.showModal) {
          if (this.$refs.closeIcon) {
            this.$refs.closeIcon.focus();
          }
          if (id) {
            this.modalUserID = id; // use in case of multi ref from v-for loop
          }
          this.modalTrapFocus();
          this.$el.addEventListener('keydown', this.modalHandleEscKey);
        } else {
          const refAfterClose = this.$refs[`linkDelete${this.modalUserID}`][0];
          if (refAfterClose) {
            refAfterClose.focus();
          }
          this.modalUserID = ''; // reset the refArray
          this.$el.removeEventListener('keydown', this.modalHandleEscKey);
        }
      });
    },
    updateValue(value, type) {
      if (type === 'roles') {
        this.roles = value.map(val => val.id);
      }
      if (type === 'statuses') {
        this.statuses = value.map(val => val.id);
      }
      if (type === 'locationGroups') {
        this.locationGroups = value.map(val => val.id);
      }
      if (type === 'districts') {
        this.districts = value.map(val => val.code);
      }
      if (type === 'schools') {
        this.schoolDistrictCodes = value.map(val => `${val.code}:${val.districtCode}`);
      }
    },
  },
  filters: {
    formatDate(value) {
      if (value) {
        return moment(value).format('MM/DD/YYYY');
      }
      return null;
    },
  },
};
</script>

<style lang="sass" scoped>
  /deep/ .filter-section.columns
    padding-left: 0
    .field
      .select
        width: 100%
        select
          width: 100%
    .column
      border-right: 1px dashed #ccc
      padding: 0 .75rem
    .column:first-child
      padding: 0 .75rem 0 0
    .column:last-child
      border: 0
      padding: 0 0 0 .75rem
  .input
    width: 375px
  .form-subtext
    color: #757575
    font-style: italic
    font-size: .85rem
  .filter-container
    padding: 1.5rem
    width: 493px
  .action-container
    width: 190px
  .status-column
    padding-left: 1.5em
  .sort-by
    padding-right: 18px
    position: relative
    color: #777
    text-decoration: none
    &:before, &:after
      border: 4px solid transparent
      content: ""
      display: block
      height: 0
      right: 5px
      top: 50%
      position: absolute
      width: 0
    &:before
      border-bottom-color: #999
      margin-top: -9px
    &:after
      border-top-color: #999
      margin-top: 1px
  .no-result-container
    padding: 6.5rem
  .center-icon
    vertical-align: middle
  .mr-2
    margin-right: .5rem
  .modal-card-head
    background-color: #F5F5F5
    text-align: left
  .modal-card-title
    margin-bottom: 0 !important
  .modal-card-body
    border-bottom-right-radius: .4rem
    border-bottom-left-radius: .4rem
    text-align: left
  .modal-body
    margin-bottom: 1rem
    display: flex
    justify-content: flex-start
  .update-box
    display: flex
    align-items: center
    justify-content: flex-end
    button
      margin-left: 1rem
  .max-width-20
    white-space: nowrap
    overflow: hidden
    text-overflow: ellipsis
    max-width: 20ch
  .table-wrapper
    overflow-x: auto
  .pager-container
    border-bottom: 1px solid #dee2e6
    border-width: 1px 0 0
    padding: 5.6px 12px
    background-color: #f9f9f9
</style>
